// Shared parts of the CRM 2.0 booking cards (M1-F10) — drawn as prototype 07:
// chip(), dayRing() (Stage 1), ring2() (Agreement tabs), tracker bars, lid(), dates.
import React from "react";
import css from "./Crm2Cards.module.css";

export function Chip({ text, tone, dot = true }) {
  if (!text) return null;
  return (
    <span className={`${css.chip} ${css[tone] || ""}`}>
      {dot && <i className={css.dot} />}
      {text}
    </span>
  );
}

// 202406110003 -> VJ20240611-0003 (prototype lid)
export function lid(id) {
  return /^\d{12}$/.test(String(id)) ? "VJ" + String(id).slice(0, 8) + "-" + String(id).slice(8) : id || "";
}

// "Fri Oct 09 2026, 09:30 AM" (prototype s1Fmt)
export function fmt(ms) {
  if (!ms) return "";
  const d = new Date(Number(ms));
  if (Number.isNaN(d.getTime())) return "";
  const date = d.toDateString();
  const time = d.toLocaleTimeString("en-US", { hour: "2-digit", minute: "2-digit", hour12: true });
  return `${date}, ${time}`;
}
export const fmtDay = (ms) => fmt(ms).split(",")[0];

export function inr(n) {
  return "₹" + Math.round(Number(n) || 0).toLocaleString("en-IN");
}

function Ring({ frac, col, num, lab, title }) {
  const R = 22;
  const C = 2 * Math.PI * R;
  const size = String(num).length > 3 ? 10.5 : String(num).length > 2 ? 12 : 16;
  return (
    <div style={{ display: "flex", alignItems: "center", gap: ".8rem" }} title={title}>
      <svg width="56" height="56" viewBox="0 0 56 56" aria-hidden="true">
        <circle cx="28" cy="28" r={R} fill="none" stroke="#EAECF0" strokeWidth="5" />
        <circle cx="28" cy="28" r={R} fill="none" stroke={col} strokeWidth="5" strokeLinecap="round" strokeDasharray={`${(C * frac).toFixed(1)} ${C.toFixed(1)}`} transform="rotate(-90 28 28)" />
        <text x="28" y="33" textAnchor="middle" fontSize={size} fontWeight="600" fill={col} fontFamily="Inter,system-ui">{num}</text>
      </svg>
      <span style={{ fontSize: "1.2rem", lineHeight: "1.5rem", color: col, maxWidth: "7.5rem" }}>{lab}</span>
    </div>
  );
}

const BAND_COL = { RED: "#D92D20", AMBER: "#DC6803", OK: "#039855", BLUE: "#155EEF" };

// Stage 1: 15 days from Day 0 (prototype dayRing). ring = { started, daysLeft, total, band, day }
export function DayRing({ ring }) {
  if (!ring || !ring.started) return <Ring frac={0} col="#98A2B3" num="–" lab="not started" title="15-day clock not started" />;
  const left = ring.daysLeft;
  return <Ring frac={Math.max(0, Math.min(1, left / ring.total))} col={BAND_COL[ring.band]} num={left} lab={left === 1 ? "day left" : "days left"} title={`Day ${ring.day} of ${ring.total}`} />;
}

// Agreement tabs (prototype ring2). ring = { kind: STAGE2 | APPOINTMENT | STOPPED, ... }
export function Ring2({ ring, apptText }) {
  if (!ring) return null;
  if (ring.kind === "STOPPED")
    return (
      <div style={{ display: "flex", alignItems: "center", gap: ".8rem" }} title="Stage 2 clock stopped when the final draft was confirmed">
        <svg width="56" height="56" viewBox="0 0 56 56" aria-hidden="true">
          <circle cx="28" cy="28" r="22" fill="none" stroke="#D1FADF" strokeWidth="5" />
          <path d="M19 28.5l6 6 12-13" fill="none" stroke="#039855" strokeWidth="3.2" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
        <span style={{ fontSize: "1.2rem", lineHeight: "1.5rem", color: "#039855", maxWidth: "7rem" }}>clock stopped</span>
      </div>
    );
  if (ring.kind === "APPOINTMENT") {
    const n = ring.daysTo;
    const col = BAND_COL[ring.band];
    if (n < 0) return <Ring frac={1} col={col} num={-n} lab={-n === 1 ? "day overdue" : "days overdue"} title={`Appointment was on ${apptText}; registration not recorded`} />;
    if (n === 0) return <Ring frac={1} col={col} num="Today" lab="registration" title="Registration appointment today" />;
    return <Ring frac={Math.max(0.08, 1 - n / 14)} col={col} num={n} lab={n === 1 ? "day to registration" : "days to registration"} title={`Registration on ${apptText}`} />;
  }
  const left = ring.daysLeft;
  return (
    <Ring
      frac={Math.max(0, Math.min(1, left / ring.total))}
      col={BAND_COL[ring.band]}
      num={left}
      lab={left === 1 ? "day left" : "days left"}
      title={`Day ${ring.day} of ${ring.total} (Stage 2 days, until the final draft is confirmed)`}
    />
  );
}

// Tracker bars: [[label, v]] v = 0 not yet · 1 in progress · 2 done · 3 error
export function Bars({ steps, title }) {
  const bg = ["#EAECF0", "#FDB022", "#12B76A", "#F04438"];
  return (
    <div style={{ display: "grid", gridTemplateColumns: `repeat(${steps.length},minmax(0,1fr))`, gap: ".4rem" }} title={title}>
      {steps.map(([l, v]) => (
        <div key={l} style={{ textAlign: "center" }}>
          <div style={{ height: ".5rem", borderRadius: ".3rem", background: bg[v] }} />
          <span style={{ fontSize: "1.1rem", color: v === 3 ? "#B42318" : v ? "#344054" : "#98A2B3" }}>
            {v === 2 ? "✓ " : v === 3 ? "! " : ""}
            {l}
          </span>
        </div>
      ))}
    </div>
  );
}

// Step status from the list row (Crm2BookingSteps): DONE / NA count as done.
export function stepDone(card, stage, code) {
  return (card.steps || []).some((s) => s.stage === stage && s.stepCode === code && ["DONE", "NA"].includes(s.status));
}
export function stepOpen(card, stage, code) {
  return (card.steps || []).some((s) => s.stage === stage && s.stepCode === code && ["PENDING", "REOPENED"].includes(s.status));
}

export function apptText(a) {
  if (!a || !a.date) return "";
  return `${new Date(Number(a.date)).toDateString()} · ${Number(a.half) === 1 ? "1st half" : "2nd half"}`;
}

export function leadLabel(card) {
  return card.leadSubType ? `${card.leadType}-${card.leadSubType}` : card.leadType || "";
}

export function unitLine(card) {
  return `${[card.wingName, card.unitNo].filter(Boolean).join(" ")} | ${card.projectName || ""}`;
}

export function planTitle(card) {
  const p = card.paymentPlan;
  if (!p) return "";
  if (typeof p === "string") return p;
  return p.title || p.name || "";
}

// Stage 1 facts shared by the card chips and Filter by (M1-F11).
const n0 = (v) => Number(v) || 0;
export function ocrText(c) {
  const bsp = n0(c.unitCost?.bsp);
  const p = bsp ? (n0(c.paidAmt?.ocr) * 100) / bsp : 0;
  if (p < 10) return "OCR < 10%";
  return Math.abs(p - 10) < 0.005 ? "OCR = 10%" : "OCR > 10%";
}
export function avChanged(c) {
  const nb = c.newUnitCost?.bsp;
  return c.crmStatusId === 3 || (nb != null && n0(nb) !== n0(c.oldUnitCost?.bsp ?? c.unitCost?.bsp));
}
export const sdrDone = (c) => n0(c.unitCost?.sdr) > 0 && n0(c.paidAmt?.sdr) >= n0(c.unitCost?.sdr);
export const kycDone = (c) => c.isAgreementFormFilled === true;
export const afbConfirmed = (c) => stepDone(c, 1, "AFB_CONFIRMED");
